import React, { createContext, useContext } from 'react';
import { Box } from '@mui/material';
import type { GuideService } from '@modules/firebase-core/services/GuideService';
import { WithGuideHelp } from './WithGuideHelp';

export interface GuideAnchorContextDefinition {
  guideKey: string;
  testId: string;
}

export interface GuideAnchorContextValue {
  guideService: GuideService;
  anchorsByTestId: Record<string, GuideAnchorContextDefinition>;
}

const GuideAnchorContext = createContext<GuideAnchorContextValue | null>(null);

export interface GuideAnchorProviderProps extends GuideAnchorContextValue {
  children: React.ReactNode;
}

export const GuideAnchorProvider: React.FC<GuideAnchorProviderProps> = ({
  guideService,
  anchorsByTestId,
  children,
}) => (
  <GuideAnchorContext.Provider value={{ guideService, anchorsByTestId }}>
    {children}
  </GuideAnchorContext.Provider>
);

export function useGuideAnchor(ownerTestId?: string): GuideAnchorContextValue & { anchor: GuideAnchorContextDefinition } | null {
  const context = useContext(GuideAnchorContext);
  if (!context || !ownerTestId) return null;

  const anchor = context.anchorsByTestId[ownerTestId];
  if (!anchor) return null;

  return {
    ...context,
    anchor,
  };
}

export interface GuideAnchorHelpProps {
  ownerTestId?: string;
}

export const GuideAnchorHelp: React.FC<GuideAnchorHelpProps> = ({ ownerTestId }) => {
  const resolved = useGuideAnchor(ownerTestId);
  if (!resolved) return null;

  return (
    <WithGuideHelp
      guideKey={resolved.anchor.guideKey}
      guideService={resolved.guideService}
      data-testid={resolved.anchor.testId}
      position="inline-end"
      iconSize="small"
      showPreviewOnHover={false}
      stopClickPropagation
      // Section-header anchor renders inside the AccordionSummary <button>; render the
      // help icon as a <span> to avoid a <button>-in-<button> DOM-nesting violation.
      iconComponent="span"
    >
      <Box component="span" sx={{ width: 0, height: 0, overflow: 'hidden' }} />
    </WithGuideHelp>
  );
};

